import type Handsontable from 'handsontable/base';
import { cloneDeep } from 'lodash';

import { TableChangeSource } from '../../../../../common/table/TableTypes';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import { buildHotTableContentData } from '../../bridge/utils/hotInstanceUtils/hotTableDataUtils';
import { applyExactHotTableCellDataChanges, renderHotTable } from '../../bridge/utils/hotInstanceUtils/hotTableUpdateUtils';
import { clearUndoRedoCellDataReplay } from '../../bridge/utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';
import { recursivelyInferTypeForFormulaCell } from '../utils/hotInstanceUtils/cell/hotTableFormulaTypeInferenceUtils';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

export const FORMULA_TYPE_INFERENCE_PLUGIN_NAME = 'FormulaTypeInference';

/**
 * Re-infers formula cell metadata after HOT changes settle, including dependent formula cells.
 */
export default class FormulaTypeInferenceHotPlugin extends MilanoteHotPlugin {
    static get PLUGIN_KEY(): string {
        return FORMULA_TYPE_INFERENCE_PLUGIN_NAME;
    }

    enablePlugin(): void {
        this.addHook('afterChange', (changes, source) => this.onAfterChange(changes, source));

        super.enablePlugin();
    }

    onAfterChange(changes: Handsontable.CellChange[] | null, source?: Handsontable.ChangeSource): void {
        if (!changes) return;

        const tableSource = source as TableChangeSource | undefined;

        const shouldInferTypesForFormulaCells =
            tableSource !== TableChangeSource.LOAD_DATA &&
            tableSource !== TableChangeSource.UPDATE_DATA &&
            tableSource !== TableChangeSource.CELL_META_UPDATE_CELL_TYPE_EFFECT;

        const changeApplied = shouldInferTypesForFormulaCells && this.inferTypesForFormulaCells(changes, tableSource);

        // If change is applied, render should already happen after change is made, so do not render again
        if (changeApplied) return;

        renderHotTable(this.milanoteHot);
    }

    inferTypesForFormulaCells(changes: Handsontable.CellChange[], tableSource: TableChangeSource | undefined): boolean {
        const { elementId, locale, undoRedoRichCellDataState } = this.milanoteHot.milanoteProps;

        if (tableSource === TableChangeSource.UNDO || tableSource === TableChangeSource.REDO) {
            clearUndoRedoCellDataReplay(undoRedoRichCellDataState);
        }

        const sheet = hyperFormulaInstance.getSheetId(elementId);
        if (sheet === undefined) return false;

        const data = cloneDeep(buildHotTableContentData(this.milanoteHot));
        const visited = new WeakMap<object, boolean>();

        // This is supposed to determine any dependant cell that's impacted by a type change in a referenced
        //  cell and update the type of the dependant (see https://github.com/Milanote/milanote/pull/14866)
        // NOTE: There appears to be a bug in this logic - see https://linear.app/milanote/issue/WEB-15825/dependent-cell-types-no-longer-update
        const cellDataChanges = changes.flatMap(([row, col]) =>
            recursivelyInferTypeForFormulaCell({
                cellAddress: { sheet, row, col: Number(col) },
                level: 0,
                tableSource,
                hotInstance: this.milanoteHot,
                data,
                locale,
                visited,
            }),
        );

        if (cellDataChanges.length === 0) return false;

        applyExactHotTableCellDataChanges(
            this.milanoteHot,
            cellDataChanges,
            TableChangeSource.CELL_META_UPDATE_CELL_TYPE_EFFECT,
        );

        return true;
    }
}
